import { CLIENT_COSTS } from "../common/Const";
import {
  formatDayOfWeek,
  getClassType,
  getClassTypeName,
  getClientChargeClassType,
  getPersonName,
} from "../common/Helpers";
import { CLIENT_LEDGER_ENTRY_TYPES } from "../manage/clientLedgerTypes";
import { createClientLedgerEntryKey } from "../manage/clientLedgerValidation";

function monthStartDate(month) {
  return `${month}-01`;
}

function monthEndDate(month) {
  const [year, monthNumber] = month.split("-").map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();
  return `${month}-${String(lastDay).padStart(2, "0")}`;
}

function isInMonth(date, month) {
  return date >= monthStartDate(month) && date <= monthEndDate(month);
}

function getStudentClientId(studentId, students) {
  const student = students.find(s => s.id === studentId);
  return student ? student.clientId : null;
}

function getStudentRecord(studentId, students) {
  return students.find(student => student.id === studentId) || null;
}

function getStatus(balance) {
  if (balance > 0) return "nadplata";
  if (balance < 0) return "zaleglosc";
  return "rozliczony";
}

function findApprovedMonthlyCharge(ledger, clientId, month) {
  return ledger.find(entry => (
    entry.clientId === clientId &&
    entry.type === CLIENT_LEDGER_ENTRY_TYPES.MONTHLY_CHARGE &&
    entry.settlementMonth === month
  ));
}

export function calculateLedgerBalanceUntil(ledger, clientId, beforeDate) {
  return ledger
    .filter(entry => entry.clientId === clientId && entry.date < beforeDate)
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
}

function calculatePaymentsInMonth(ledger, clientId, month) {
  return ledger
    .filter(entry => entry.clientId === clientId)
    .filter(entry => entry.type === CLIENT_LEDGER_ENTRY_TYPES.PAYMENT)
    .filter(entry => isInMonth(entry.date, month))
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
}

function getTeacherName(teacherId, teachers = []) {
  if (!teacherId) {
    return "";
  }

  const teacher = teachers.find(item => item.id === teacherId);
  return teacher ? getPersonName(teacher.id, teachers) : teacherId;
}

function buildLessonSnapshotItem({
  lesson,
  student,
  classType,
  rate,
  charged,
  reason,
  students,
  clients,
  teachers,
  studentIndex,
  studentsPresent,
  studentsTotal,
}) {
  const clientId = getStudentClientId(student.id, students);
  const client = clients.find(c => c.id === clientId);
  const lessonStudent = studentIndex != null && lesson.students ? lesson.students[studentIndex] : undefined;
  const { date: lessonDate, startTime, teacherId } = lesson.lessonDetails;

  return {
    lessonRegisterId: lesson.id,
    lessonId: lesson.lessonId,
    snapshotKey: `${lesson.id}#${student.id}#${studentIndex != null ? studentIndex : "na"}`,
    date: lessonDate,
    dayOfWeekName: formatDayOfWeek(new Date(lessonDate)),
    startTime,
    classType,
    classTypeName: getClassTypeName(classType),
    teacherId,
    teacherName: getTeacherName(teacherId, teachers),
    studentId: student.id,
    studentName: getPersonName(student.id, students),
    clientId,
    clientName: client ? `${client.firstName} ${client.lastName}` : clientId,
    rate,
    amount: charged ? rate : 0,
    charged,
    reason,
    isPresent: lessonStudent ? lessonStudent.isPresent : undefined,
    studentsPresent,
    studentsTotal,
  };
}

function findLessonDetailsForSnapshotItem(item, lessonRegister) {
  const lessonByRegisterId = lessonRegister.find(lesson => lesson.id === item.lessonRegisterId);
  if (lessonByRegisterId && lessonByRegisterId.lessonDetails) {
    return lessonByRegisterId.lessonDetails;
  }

  const lessonByKey = lessonRegister.find(lesson => (
    lesson.lessonId === item.lessonId &&
    lesson.lessonDetails &&
    lesson.lessonDetails.date === item.date &&
    (lesson.lessonDetails.startTime || "") === (item.startTime || "")
  ));

  return lessonByKey ? lessonByKey.lessonDetails : null;
}

function enrichSnapshotItem(item, lessonRegister, teachers = []) {
  if (item.dayOfWeekName && item.teacherName) {
    return item;
  }

  const lessonDetails = findLessonDetailsForSnapshotItem(item, lessonRegister);
  if (!lessonDetails) {
    return item;
  }

  const nextDate = item.date || lessonDetails.date;
  const nextTeacherId = item.teacherId || lessonDetails.teacherId;

  return {
    ...item,
    date: nextDate,
    dayOfWeekName: item.dayOfWeekName || (nextDate ? formatDayOfWeek(new Date(nextDate)) : ""),
    startTime: item.startTime || lessonDetails.startTime,
    teacherId: nextTeacherId,
    teacherName: item.teacherName || getTeacherName(nextTeacherId, teachers),
  };
}

function enrichSnapshot(snapshot, lessonRegister, teachers = []) {
  return {
    ...snapshot,
    charged: (snapshot.charged || []).map(item => enrichSnapshotItem(item, lessonRegister, teachers)),
    skipped: (snapshot.skipped || []).map(item => enrichSnapshotItem(item, lessonRegister, teachers)),
  };
}

function getSettlementDuplicateKey(item) {
  return `${item.clientId}#${item.lessonId}#${item.date}#${item.startTime || ""}#${item.studentId}`;
}

function getConflictType(items) {
  const attendanceStates = new Set(items.map(item => String(item.isPresent)));
  if (attendanceStates.size > 1) {
    return "attendance";
  }

  return "details";
}

function areItemsEquivalent(items) {
  const presenceStates = new Set(items.map(item => String(item.isPresent)));
  const amounts = new Set(items.map(item => String(item.amount)));
  const reasons = new Set(items.map(item => item.reason));

  return presenceStates.size === 1 && amounts.size === 1 && reasons.size === 1;
}

function buildDuplicateConflictWarnings(items) {
  const itemsByDuplicateKey = items.reduce((groupedItems, item) => {
    const duplicateKey = getSettlementDuplicateKey(item);
    const existingItems = groupedItems[duplicateKey] || [];

    return {
      ...groupedItems,
      [duplicateKey]: [...existingItems, item],
    };
  }, {});

  return Object.values(itemsByDuplicateKey).reduce((warnings, groupedItems) => {
    if (groupedItems.length <= 1 || areItemsEquivalent(groupedItems)) {
      return warnings;
    }

    return [...warnings, {
      reason: "duplicateConflict",
      conflictType: getConflictType(groupedItems),
      duplicateKey: getSettlementDuplicateKey(groupedItems[0]),
      date: groupedItems[0].date,
      startTime: groupedItems[0].startTime,
      studentName: groupedItems[0].studentName,
      items: groupedItems.map(item => ({
        lessonRegisterId: item.lessonRegisterId,
        snapshotKey: item.snapshotKey,
        isPresent: item.isPresent,
        amount: item.amount,
        reason: item.reason,
      })),
    }];
  }, []);
}

function stripInternalDuplicateFields(item) {
  const cleanItem = { ...item };
  delete cleanItem._bucket;
  delete cleanItem._order;
  return cleanItem;
}

function dedupeSettlementSnapshot({ charged, skipped, warnings }) {
  const combinedItems = [
    ...charged.map((item, index) => ({ ...item, _bucket: "charged", _order: index })),
    ...skipped.map((item, index) => ({ ...item, _bucket: "skipped", _order: charged.length + index })),
  ];
  const itemsByDuplicateKey = combinedItems.reduce((groupedItems, item) => {
    const duplicateKey = getSettlementDuplicateKey(item);
    const existingItems = groupedItems[duplicateKey] || [];

    return {
      ...groupedItems,
      [duplicateKey]: [...existingItems, item],
    };
  }, {});

  return Object.values(itemsByDuplicateKey).reduce((result, groupedItems) => {
    const sortedItems = [...groupedItems].sort((left, right) => left._order - right._order);

    if (sortedItems.length === 1) {
      const [singleItem] = sortedItems;
      result[singleItem._bucket].push(stripInternalDuplicateFields(singleItem));
      return result;
    }

    if (!areItemsEquivalent(sortedItems)) {
      sortedItems.forEach(item => {
        result[item._bucket].push(stripInternalDuplicateFields(item));
      });
      result.warnings.push(...buildDuplicateConflictWarnings(sortedItems));
      return result;
    }

    const [firstItem, ...duplicateItems] = sortedItems;
    result[firstItem._bucket].push(stripInternalDuplicateFields(firstItem));
    duplicateItems.forEach(item => {
      result.skipped.push({
        ...stripInternalDuplicateFields(item),
        reason: "autoDuplicate",
        autoExcludedDuplicate: true,
      });
    });
    return result;
  }, { charged: [], skipped: [], warnings: [...warnings] });
}

export function collectMissingClientWarnings({ month, lessonRegister, students }) {
  return lessonRegister
    .filter(lesson => lesson.lessonDetails && isInMonth(lesson.lessonDetails.date, month))
    .flatMap(lesson => (lesson.students || []).flatMap(lessonStudent => {
      const studentRecord = getStudentRecord(lessonStudent.id, students);

      if (studentRecord && studentRecord.clientId) {
        return [];
      }

      return [{
        reason: "missingClient",
        lessonRegisterId: lesson.id,
        lessonId: lesson.lessonId,
        date: lesson.lessonDetails.date,
        startTime: lesson.lessonDetails.startTime,
        studentId: lessonStudent.id,
        lessonStudent,
        studentRecord,
      }];
    }));
}

function calculateClientLessonItems({ client, month, lessonRegister, students, schoolClasses, clients, teachers = [] }) {
  const charged = [];
  const skipped = [];
  const warnings = [];

  const resolveClassType = lesson => {
    const classTypeFromSchoolClass = getClassType(lesson.lessonId, schoolClasses);
    if (classTypeFromSchoolClass !== "Nieznany typ lekcji") {
      return classTypeFromSchoolClass;
    }

    // Keep historical lesson registers billable even if their school class entry no longer exists.
    return (lesson.lessonDetails && (lesson.lessonDetails.classTypeId || lesson.lessonDetails.classType)) || classTypeFromSchoolClass;
  };

  lessonRegister
    .filter(lesson => lesson.lessonDetails && isInMonth(lesson.lessonDetails.date, month))
    .forEach(lesson => {
      const studentsPresent = (lesson.students || []).filter(student => student.isPresent).length;
      const classType = getClientChargeClassType(resolveClassType(lesson), studentsPresent);
      const rate = CLIENT_COSTS[classType];

      (lesson.students || []).forEach(student => {
        const clientId = getStudentClientId(student.id, students);

        if (!clientId) {
          return;
        }

        if (clientId !== client.id) return;

        if (rate === undefined) {
          warnings.push({ lessonRegisterId: lesson.id, reason: "missingRate", classType: classType || "Nieznany typ lekcji" });
          return;
        }

        const studentIndex = (lesson.students || []).indexOf(student);
        const snapshotItem = buildLessonSnapshotItem({
          lesson,
          student,
          studentIndex,
          classType,
          rate,
          charged: !lesson.lessonDetails.doNotCharge,
          reason: lesson.lessonDetails.doNotCharge ? "doNotCharge" : "charged",
          students,
          clients,
          teachers,
          studentsPresent,
          studentsTotal: (lesson.students || []).length,
        });

        if (lesson.lessonDetails.doNotCharge) {
          skipped.push(snapshotItem);
        } else {
          charged.push(snapshotItem);
        }
      });
    });

  return dedupeSettlementSnapshot({ charged, skipped, warnings });
}

export function calculateClientSettlement({ client, month, ledger, lessonRegister, students, schoolClasses, clients = [client], teachers = [] }) {
  const openingBalance = calculateLedgerBalanceUntil(ledger, client.id, monthStartDate(month));
  const paymentsInMonth = calculatePaymentsInMonth(ledger, client.id, month);
  const approvedCharge = findApprovedMonthlyCharge(ledger, client.id, month);
  const calculatedItems = calculateClientLessonItems({ client, month, lessonRegister, students, schoolClasses, clients, teachers });
  const monthlyChargeAmount = approvedCharge
    ? Math.abs(Number(approvedCharge.amount || 0))
    : calculatedItems.charged.reduce((sum, item) => sum + item.amount, 0);
  const closingBalance = openingBalance + paymentsInMonth - monthlyChargeAmount;
  const snapshot = approvedCharge || {
    snapshot: {
      month,
      charged: calculatedItems.charged,
      skipped: calculatedItems.skipped,
      warnings: calculatedItems.warnings,
    },
  };
  const enrichedSnapshot = enrichSnapshot(snapshot.snapshot, lessonRegister, teachers);

  return {
    clientId: client.id,
    clientName: `${client.firstName || ""} ${client.lastName || ""}`.trim(),
    month,
    openingBalance,
    paymentsInMonth,
    monthlyChargeAmount,
    closingBalance,
    status: getStatus(closingBalance),
    approved: !!approvedCharge,
    approvalStatus: approvedCharge ? "zatwierdzone" : "robocze",
    snapshot: enrichedSnapshot,
  };
}

export function calculateClientSettlements({ clients, month, ledger, lessonRegister, students, schoolClasses, teachers = [] }) {
  return clients
    .filter(client => !client.deleted)
    .map(client => calculateClientSettlement({ client, month, ledger, lessonRegister, students, schoolClasses, clients, teachers }));
}

export function buildMonthlyChargeEntry(settlement) {
  const date = monthEndDate(settlement.month);
  const entryKey = createClientLedgerEntryKey(date, CLIENT_LEDGER_ENTRY_TYPES.MONTHLY_CHARGE, "SETTLEMENT");

  return {
    id: `${settlement.clientId}#${entryKey}`,
    clientId: settlement.clientId,
    entryKey,
    type: CLIENT_LEDGER_ENTRY_TYPES.MONTHLY_CHARGE,
    date,
    settlementMonth: settlement.month,
    amount: -Math.abs(settlement.monthlyChargeAmount),
    method: "",
    note: `Rozliczenie ${settlement.month}`,
    snapshot: settlement.snapshot,
  };
}

export function getSettlementItemKey(item) {
  return item.snapshotKey || `${item.lessonRegisterId}#${item.studentId}`;
}

const exclusionKey = (clientId, item) => `${clientId}#${getSettlementItemKey(item)}`;

function isToggled(clientId, item, excludedKeys) {
  return !!(excludedKeys && excludedKeys[exclusionKey(clientId, item)]);
}

export function recalcWithExclusions(settlement, month, excludedKeys) {
  const chargedItems = settlement.snapshot.charged || [];
  const skippedItems = settlement.snapshot.skipped || [];
  const activeCharged = chargedItems.filter(item => !isToggled(settlement.clientId, item, excludedKeys));
  const excludedItems = chargedItems.filter(item => isToggled(settlement.clientId, item, excludedKeys));
  const includedSkippedItems = skippedItems.filter(item => isToggled(settlement.clientId, item, excludedKeys));
  const activeSkippedItems = skippedItems.filter(item => !isToggled(settlement.clientId, item, excludedKeys));
  const recalculatedCharged = [...activeCharged, ...includedSkippedItems];
  const monthlyChargeAmount = recalculatedCharged.reduce((sum, item) => sum + item.amount, 0);
  const closingBalance = settlement.openingBalance + settlement.paymentsInMonth - monthlyChargeAmount;

  const activeSkipped = [...activeSkippedItems, ...excludedItems];
  activeSkipped.sort((a, b) => a.date.localeCompare(b.date));
  recalculatedCharged.sort((a, b) => a.date.localeCompare(b.date));
  const persistentWarnings = (settlement.snapshot.warnings || []).filter(warning => warning.reason !== "duplicateConflict");
  const duplicateConflictWarnings = buildDuplicateConflictWarnings(recalculatedCharged);

  return {
    ...settlement,
    month,
    monthlyChargeAmount,
    closingBalance,
    status: getStatus(closingBalance),
    snapshot: {
      ...settlement.snapshot,
      charged: recalculatedCharged,
      skipped: activeSkipped,
      warnings: [...persistentWarnings, ...duplicateConflictWarnings],
    },
  };
}
